import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckCircle2, MapPin, Award, Play, FileText } from 'lucide-react';
import { VideoModal } from './VideoModal';
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  return <section className="relative w-full pt-40 pb-28 lg:pt-52 lg:pb-24 overflow-hidden" data-id="element-351">
      {/* Image Background */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-352">
        <img src="https://cdn.magicpatterns.com/uploads/gHgy89W3EcSExnvbgaMzow/hero_bg5.jpg" alt="Real estate professional" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-353" />
      </div>

      {/* Heavy Dark Base Layer - Reduced by 15% (70% to 55%) */}
      <div className="absolute inset-0 bg-gray-950/55" data-id="element-354" />

      {/* Purple Tint Layer - Keep same */}
      <div className="absolute inset-0 bg-brand-purple-900/40 mix-blend-multiply" data-id="element-355" />

      {/* Deep Gradient Vignette - Reduced by 15% (75% to 60%) */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-purple-950/60 via-transparent to-brand-purple-950/60" data-id="element-356" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-357">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-358">
          {/* Left Content */}
          <motion.div className="lg:col-span-7 space-y-8 text-center lg:text-left" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-359">
            <div className="space-y-4" data-id="element-360">
              <motion.div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mx-auto lg:mx-0" initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2
            }} data-id="element-361">
                <MapPin className="w-4 h-4 mr-1.5" data-id="element-362" />
                Arizona's Premier Real Estate School
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight" data-id="element-363">
                Arizona Real Estate <br className="hidden lg:block" data-id="element-364" />
                <span className="text-brand-orange-300" data-id="element-365">License Renewal</span>
              </h1>

              <p className="text-xl sm:text-2xl text-white font-semibold" data-id="element-366">
                Continuing Education Made Simple
              </p>

              <p className="text-lg text-white/90 max-w-2xl mx-auto lg:mx-0" data-id="element-367">
                On-Demand, Zoom, and In-person CE classes — built specifically
                for Arizona Agents and Brokers. Get your hours done quickly and
                conveniently.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-368">
              <Button size="lg" className="bg-brand-orange-400 hover:bg-brand-orange-500 text-brand-purple-900 font-bold shadow-xl hover:shadow-2xl transition-all border-none" data-id="element-369">
                View On-Demand Classes
              </Button>
              <Button size="lg" className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border-2 border-white/20 font-semibold shadow-xl" data-id="element-370">
                See Monthly Schedule
              </Button>
            </div>

            {/* Video Teaser */}
            <button onClick={() => setIsVideoOpen(true)} className="group inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-md rounded-xl border-2 border-white/20 hover:bg-white/20 hover:border-white/30 transition-all mx-auto lg:mx-0 shadow-lg" data-id="element-371">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md" data-id="element-372">
                <Play className="w-5 h-5 text-brand-purple-600 fill-brand-purple-600 ml-0.5" data-id="element-373" />
              </div>
              <div className="text-left" data-id="element-374">
                <div className="text-sm font-semibold text-white" data-id="element-375">
                  Watch a Quick Intro
                </div>
                <div className="text-xs text-white/80" data-id="element-376">
                  How license renewal works with Desert Sage
                </div>
              </div>
            </button>

            {/* Trust Signals */}
            <div className="pt-4 flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4 text-sm" data-id="element-377">
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-378">
                <CheckCircle2 className="w-4 h-4 text-green-300 mr-2" data-id="element-379" />
                <span className="text-white font-medium" data-id="element-380">
                  ADRE School #S06-0002
                </span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-381">
                <Award className="w-4 h-4 text-brand-orange-300 mr-2" data-id="element-382" />
                <span className="text-white font-medium" data-id="element-383">REEA Member</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-384">
                <MapPin className="w-4 h-4 text-white mr-2" data-id="element-385" />
                <span className="text-white font-medium" data-id="element-386">
                  Locally Owned & Operated
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - License Summaries */}
          <motion.div className="lg:col-span-5 w-full space-y-4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-387">
            <a href="/License_Information" className="block bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 hover:bg-white/20 transition-all hover:-translate-y-1 shadow-xl group" data-id="element-388">
              <div className="flex items-start" data-id="element-389">
                <div className="bg-brand-orange-400 p-3 rounded-lg mr-4 group-hover:scale-110 transition-transform" data-id="element-390">
                  <FileText className="w-6 h-6 text-brand-purple-900" data-id="element-391" />
                </div>
                <div data-id="element-392">
                  <h3 className="text-xl font-bold text-white mb-1" data-id="element-393">
                    AGENTS NEED
                  </h3>
                  <p className="text-brand-orange-300 font-medium mb-2" data-id="element-394">
                    Salesperson/Associate Broker
                  </p>
                  <div className="flex items-center text-white/90" data-id="element-395">
                    <span className="font-bold text-2xl mr-2" data-id="element-396">24 hrs.</span>
                    <span className="text-sm" data-id="element-397">every 2-year cycle</span>
                  </div>
                </div>
              </div>
            </a>

            <a href="/License_Information" className="block bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 hover:bg-white/20 transition-all hover:-translate-y-1 shadow-xl group" data-id="element-398">
              <div className="flex items-start" data-id="element-399">
                <div className="bg-brand-purple-600 p-3 rounded-lg mr-4 group-hover:scale-110 transition-transform" data-id="element-400">
                  <FileText className="w-6 h-6 text-white" data-id="element-401" />
                </div>
                <div data-id="element-402">
                  <h3 className="text-xl font-bold text-white mb-1" data-id="element-403">
                    BROKERS NEED
                  </h3>
                  <p className="text-brand-purple-300 font-medium mb-2" data-id="element-404">
                    Designated Broker / Delegated Broker / Self Employed Broker
                  </p>
                  <div className="flex items-center text-white/90" data-id="element-405">
                    <span className="font-bold text-2xl mr-2" data-id="element-406">30 hrs.</span>
                    <span className="text-sm" data-id="element-407">every 2-year cycle</span>
                  </div>
                </div>
              </div>
            </a>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal isOpen={isVideoOpen} onClose={() => setIsVideoOpen(false)} videoId="OhJuESLnDqk" title="Desert Sage Seminars — Welcome" data-id="element-408" />
    </section>;
}